import type Media from '../types/media'
import type ExperienceData from '../types/experience'
import type { PanelBlock, QuizPanel } from '../types/panel'
import type QuestionData from '../types/question'
import type { PointOfInterest } from '../components/PointOfInterest'
import { getAssetUrl } from './index'

interface CachedMedia {
  originalUrl: string
  cachedUrl: string
  media: Media
  isDataUrl: boolean
}

class MediaCacheManager {
  private cache = new Map<string, CachedMedia>()
  private pendingDownloads = new Map<string, Promise<string>>()

  async cacheExperienceMedia(
    experience: ExperienceData,
    onProgress?: (progress: number) => void
  ): Promise<ExperienceData> {
    const mediaUrls = this.extractAllMediaUrls(experience)
    await this.downloadAndCacheMedia(mediaUrls, onProgress)
    return this.replaceMediaUrls(experience)
  }

  async cachePointOfInterests(
    pois: PointOfInterest[],
    onProgress?: (progress: number) => void
  ): Promise<PointOfInterest[]> {
    const mediaObjects: Media[] = []
    this.extractPointOfInterestMedia(pois, mediaObjects)
    await this.downloadAndCacheMedia(mediaObjects, onProgress)
    return this.replacePointOfInterestMediaUrls(pois)
  }

  private extractAllMediaUrls(experience: ExperienceData): Media[] {
    const mediaObjects: Media[] = []

    if (experience.brandLogo) {
      mediaObjects.push(experience.brandLogo)
    }
    this.extractPanelMedia(experience.engineerMode, mediaObjects)
    this.extractPanelMedia(experience.driverMode, mediaObjects)

    if (experience.soundEffects) {
      this.extractSoundEffectsMedia(experience.soundEffects, mediaObjects)
    }

    if (experience.questions?.docs) {
      experience.questions.docs.forEach(question => {
        this.extractQuestionMedia(question, mediaObjects)
      })
    }

    return mediaObjects
  }

  private extractSoundEffectsMedia(
    soundEffects: ExperienceData['soundEffects'],
    mediaObjects: Media[]
  ): void {
    if (soundEffects.backgroundMusic) {
      mediaObjects.push(soundEffects.backgroundMusic)
    }
    if (soundEffects.mainButton) {
      mediaObjects.push(soundEffects.mainButton)
    }
    if (soundEffects.tabs) {
      mediaObjects.push(soundEffects.tabs)
    }
    if (soundEffects.closeButton) {
      mediaObjects.push(soundEffects.closeButton)
    }
    if (soundEffects.pointOfInterest) {
      mediaObjects.push(soundEffects.pointOfInterest)
    }
    if (soundEffects.correctAnswer) {
      mediaObjects.push(soundEffects.correctAnswer)
    }
    if (soundEffects.incorrectAnswer) {
      mediaObjects.push(soundEffects.incorrectAnswer)
    }
    if (soundEffects.driveBy) {
      mediaObjects.push(soundEffects.driveBy)
    }
  }

  private extractPanelMedia(panel: PanelBlock, mediaObjects: Media[]): void {
    if (panel.homePanel?.instructionIcon) {
      mediaObjects.push(panel.homePanel.instructionIcon)
    }

    if (panel.educatePanel?.instructionIcon) {
      mediaObjects.push(panel.educatePanel.instructionIcon)
    }

    if (panel.quizPanel) {
      panel.quizPanel.forEach((quiz: QuizPanel) => {
        if (quiz.quizInstruction?.backCtaIcon) {
          mediaObjects.push(quiz.quizInstruction.backCtaIcon)
        }
        if (quiz.quizPanel?.backCtaIcon) {
          mediaObjects.push(quiz.quizPanel.backCtaIcon)
        }
      })
    }
  }

  private extractQuestionMedia(question: QuestionData, mediaObjects: Media[]): void {
    if (question.audios?.preQuestion) {
      mediaObjects.push(question.audios.preQuestion)
    }
    if (question.audios?.correctAnswer) {
      mediaObjects.push(question.audios.correctAnswer)
    }
    if (question.audios?.incorrectAnswer) {
      mediaObjects.push(question.audios.incorrectAnswer)
    }
  }

  private extractPointOfInterestMedia(pois: PointOfInterest[], mediaObjects: Media[]): void {
    pois.forEach(poi => {
      if (poi.infoPanel?.infoTab1?.audio) {
        mediaObjects.push(poi.infoPanel.infoTab1.audio as Media)
      }
      if (poi.infoPanel?.infoTab2?.audio) {
        mediaObjects.push(poi.infoPanel.infoTab2.audio as Media)
      }
      if (poi.infoPanel?.infoTab3?.audio) {
        mediaObjects.push(poi.infoPanel.infoTab3.audio as Media)
      }
      if (poi.infoPanel?.infoTab4?.audio) {
        mediaObjects.push(poi.infoPanel.infoTab4.audio as Media)
      }
    })
  }

  private async downloadAndCacheMedia(
    mediaObjects: Media[],
    onProgress?: (progress: number) => void
  ): Promise<void> {
    if (mediaObjects.length === 0) {
      onProgress?.(100)
      return
    }

    // Calculate total expected download size
    const DEFAULT_FILE_SIZE = 1024 * 1024 // 1MB default for files without size info
    const totalSize = mediaObjects.reduce((sum, media) => {
      return sum + (media.filesize || DEFAULT_FILE_SIZE)
    }, 0)

    let totalBytesDownloaded = 0

    const updateProgress = (additionalBytes: number) => {
      totalBytesDownloaded += additionalBytes
      const progress = Math.min((totalBytesDownloaded / totalSize) * 100, 100)
      onProgress?.(progress)
    }

    // Create all download promises simultaneously for parallel execution
    const downloadPromises = mediaObjects.map(async media => {
      const fullUrl = getAssetUrl(media.filename)
      const expectedSize = media.filesize || DEFAULT_FILE_SIZE

      if (this.cache.has(fullUrl)) {
        // Cached items complete immediately - count full expected size
        updateProgress(expectedSize)
        return Promise.resolve()
      }

      if (this.pendingDownloads.has(fullUrl)) {
        // Wait for existing download
        await this.pendingDownloads.get(fullUrl)
        updateProgress(expectedSize)
        return
      }

      // Start new download with progress tracking
      const downloadPromise = this.downloadMedia(fullUrl, media, bytesDownloaded => {
        // This callback is called during download with incremental bytes
        updateProgress(bytesDownloaded)
      })
      this.pendingDownloads.set(fullUrl, downloadPromise)

      try {
        await downloadPromise
        // Final update handled by the streaming download callback
      } finally {
        this.pendingDownloads.delete(fullUrl)
      }
    })

    // Wait for all downloads to complete
    await Promise.all(downloadPromises)
  }

  private async downloadMedia(
    url: string,
    media: Media,
    onProgress?: (bytesDownloaded: number) => void
  ): Promise<string> {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.status}`)
      }

      // Get expected file size from response headers or media object
      const contentLength = response.headers.get('content-length')
      const expectedSize = media.filesize || (contentLength ? parseInt(contentLength, 10) : 0)

      // Get MIME type from response headers or media object
      const mimeType = media.mimeType || response.headers.get('content-type') || ''

      const reader = response.body?.getReader()
      if (!reader || !onProgress) {
        // Fallback to standard blob download if no reader or progress callback
        const blob = await response.blob()

        // Convert audio to data URL, keep images as blob URLs
        let cachedUrl: string
        let isDataUrl = false

        if (mimeType.startsWith('audio/')) {
          cachedUrl = await this.blobToDataUrl(blob)
          isDataUrl = true
        } else {
          cachedUrl = URL.createObjectURL(blob)
          isDataUrl = false
        }

        this.cache.set(url, {
          originalUrl: url,
          cachedUrl,
          media: { ...media },
          isDataUrl,
        })

        if (onProgress && expectedSize) {
          onProgress(expectedSize)
        }

        return cachedUrl
      }

      // Stream download with progress tracking
      const chunks: Uint8Array[] = []

      while (true) {
        const { done, value } = await reader.read()

        if (done) break

        chunks.push(value)

        // Report incremental progress
        onProgress(value.length)
      }

      // Create blob from chunks with proper MIME type
      const blob = new Blob(chunks, { type: mimeType })

      let cachedUrl: string
      let isDataUrl = false

      if (mimeType.startsWith('audio/')) {
        cachedUrl = await this.blobToDataUrl(blob)
        isDataUrl = true
      } else {
        cachedUrl = URL.createObjectURL(blob)
        isDataUrl = false
      }

      this.cache.set(url, {
        originalUrl: url,
        cachedUrl,
        media: { ...media },
        isDataUrl,
      })

      return cachedUrl
    } catch (error) {
      console.warn(`Failed to cache media ${url}:`, error)
      return url
    }
  }

  private replaceMediaUrls(experience: ExperienceData): ExperienceData {
    const clonedExperience = JSON.parse(JSON.stringify(experience)) as ExperienceData

    if (clonedExperience.brandLogo) {
      this.replaceMediaUrl(clonedExperience.brandLogo)
    }

    this.replacePanelMediaUrls(clonedExperience.engineerMode)
    this.replacePanelMediaUrls(clonedExperience.driverMode)

    if (clonedExperience.soundEffects) {
      this.replaceSoundEffectsMediaUrls(clonedExperience.soundEffects)
    }

    if (clonedExperience.questions?.docs) {
      clonedExperience.questions.docs.forEach(question => {
        this.replaceQuestionMediaUrls(question)
      })
    }

    return clonedExperience
  }

  private replaceSoundEffectsMediaUrls(soundEffects: ExperienceData['soundEffects']): void {
    if (soundEffects.backgroundMusic) {
      this.replaceMediaUrl(soundEffects.backgroundMusic)
    }
    if (soundEffects.mainButton) {
      this.replaceMediaUrl(soundEffects.mainButton)
    }
    if (soundEffects.tabs) {
      this.replaceMediaUrl(soundEffects.tabs)
    }
    if (soundEffects.closeButton) {
      this.replaceMediaUrl(soundEffects.closeButton)
    }
    if (soundEffects.pointOfInterest) {
      this.replaceMediaUrl(soundEffects.pointOfInterest)
    }
    if (soundEffects.correctAnswer) {
      this.replaceMediaUrl(soundEffects.correctAnswer)
    }
    if (soundEffects.incorrectAnswer) {
      this.replaceMediaUrl(soundEffects.incorrectAnswer)
    }
    if (soundEffects.driveBy) {
      this.replaceMediaUrl(soundEffects.driveBy)
    }
  }

  private replacePanelMediaUrls(panel: PanelBlock): void {
    if (panel.homePanel?.instructionIcon) {
      this.replaceMediaUrl(panel.homePanel.instructionIcon)
    }

    if (panel.educatePanel?.instructionIcon) {
      this.replaceMediaUrl(panel.educatePanel.instructionIcon)
    }

    if (panel.quizPanel) {
      panel.quizPanel.forEach((quiz: QuizPanel) => {
        if (quiz.quizInstruction?.backCtaIcon) {
          this.replaceMediaUrl(quiz.quizInstruction.backCtaIcon)
        }
        if (quiz.quizPanel?.backCtaIcon) {
          this.replaceMediaUrl(quiz.quizPanel.backCtaIcon)
        }
      })
    }
  }

  private replaceQuestionMediaUrls(question: QuestionData): void {
    if (question.audios?.preQuestion) {
      this.replaceMediaUrl(question.audios.preQuestion)
    }
    if (question.audios?.correctAnswer) {
      this.replaceMediaUrl(question.audios.correctAnswer)
    }
    if (question.audios?.incorrectAnswer) {
      this.replaceMediaUrl(question.audios.incorrectAnswer)
    }
  }

  private replacePointOfInterestMediaUrls(pois: PointOfInterest[]): PointOfInterest[] {
    return pois.map(poi => {
      const clonedPoi = JSON.parse(JSON.stringify(poi)) as PointOfInterest
      if (clonedPoi.infoPanel?.infoTab1?.audio) {
        this.replaceMediaUrl(clonedPoi.infoPanel.infoTab1.audio as Media)
      }
      if (clonedPoi.infoPanel?.infoTab2?.audio) {
        this.replaceMediaUrl(clonedPoi.infoPanel.infoTab2.audio as Media)
      }
      if (clonedPoi.infoPanel?.infoTab3?.audio) {
        this.replaceMediaUrl(clonedPoi.infoPanel.infoTab3.audio as Media)
      }
      if (clonedPoi.infoPanel?.infoTab4?.audio) {
        this.replaceMediaUrl(clonedPoi.infoPanel.infoTab4.audio as Media)
      }
      return clonedPoi
    })
  }

  private replaceMediaUrl(media: Media): void {
    const fullUrl = getAssetUrl(media.filename)
    const cached = this.cache.get(fullUrl)

    if (cached) {
      media.url = cached.cachedUrl
    }
  }

  private async blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  cleanup(): void {
    this.cache.forEach(cached => {
      // Only revoke blob URLs, not data URLs
      if (!cached.isDataUrl) {
        URL.revokeObjectURL(cached.cachedUrl)
      }
    })
    this.cache.clear()
    this.pendingDownloads.clear()
  }
}

export const mediaCacheManager = new MediaCacheManager()

export const cacheExperienceMedia = (
  experience: ExperienceData,
  onProgress?: (progress: number) => void
): Promise<ExperienceData> => {
  return mediaCacheManager.cacheExperienceMedia(experience, onProgress)
}

export const cachePointOfInterests = (
  pois: PointOfInterest[],
  onProgress?: (progress: number) => void
): Promise<PointOfInterest[]> => {
  return mediaCacheManager.cachePointOfInterests(pois, onProgress)
}
